/**
 * Retrieves the translation of text.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-i18n/
 */
import { __ } from '@wordpress/i18n';

/**
 * React hook that is used to mark the block wrapper element.
 * It provides all the necessary props like the class name.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-block-editor/#useblockprops
 */

import { 
	InspectorControls,
	BlockControls,
	AlignmentControl,
	useBlockProps,
} from "@wordpress/block-editor";

import { Fragment, useEffect, useState  } from "@wordpress/element";

import FontIconPicker from '@fonticonpicker/react-fonticonpicker';
import '@fonticonpicker/react-fonticonpicker/dist/fonticonpicker.base-theme.react.css';
import '@fonticonpicker/react-fonticonpicker/dist/fonticonpicker.material-theme.react.css';
import {
	PanelBody,
	TextControl,
	Button,
	Modal,
	RadioControl,
	TextareaControl,
	__experimentalBorderBoxControl as BorderBoxControl

} from "@wordpress/components";

/**
 * Lets webpack process CSS, SASS or SCSS files referenced in JavaScript files.
 * Those files can contain any CSS code that gets applied to the editor.
 *
 * @see https://www.npmjs.com/package/@wordpress/scripts#using-css
 */
import './editor.scss';
import './font-awesome/css/fontawesome-all.min.css';

/**
 * The edit function describes the structure of your block in the context of the
 * editor. This represents what the editor will render when the block is used.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-edit-save/#edit
 *
 * @return {WPElement} Element to render.
 */
export default function Edit( props ) {

	const { setAttributes, attributes } = props;
	const { icon, linkURL, textAlign, iconType, style, customSVG  } = attributes;
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	// const [tempSVG, setTempSVG] = useState(iconType === 'customsvg' ? icon : '');
	const icons = {
		'Accessibility': [
			'fas fa-wheelchair',
			'fas fa-universal-access',
			'fas fa-tty',
			'fas fa-sign-language',
			'fas fa-question-circle',
			'fas fa-phone-volume',
			'fas fa-low-vision',
			'fas fa-deaf',
			'fas fa-closed-captioning',
			'fas fa-braille',
			'fas fa-blind',
			'fas fa-audio-description',
			'fas fa-assistive-listening-systems',
			'fas fa-american-sign-language-interpreting',
			'fab fa-accessible-icon',
			'far fa-question-circle',
			'far fa-closed-captioning',
		],
		'Alert': [
			'fas fa-skull-crossbones',
			'fas fa-exclamation-triangle',
			'fas fa-exclamation-circle',
			'fas fa-exclamation',
			'fas fa-bell-slash',
			'fas fa-bell',
			'far fa-bell-slash',
			'far fa-bell',
		],
		'Animal': [
			'fas fa-spider',
			'fas fa-paw',
			'fas fa-otter',
			'fas fa-kiwi-bird',
			'fas fa-horse',
			'fas fa-hippo',
			'fas fa-frog',
			'fas fa-fish',
			'fas fa-feather-alt',
			'fas fa-feather',
			'fas fa-dragon',
			'fas fa-dove',
			'fas fa-dog',
			'fas fa-crow',
			'fas fa-cat',
		],
		'Arrows': [
			'fas fa-upload',
			'fas fa-undo-alt',
			'fas fa-text-width',
			'fas fa-text-height',
			'fas fa-sync-alt',
			'fas fa-sync',
			'fas fa-sort-up',
			'fas fa-sort-numeric-up',
			'fas fa-sort-numeric-down',
			'fas fa-sort-down',
			'fas fa-sort-amount-up',
			'fas fa-sort-amount-down',
			'fas fa-sort-alpha-up',
			'fas fa-sort-alpha-down',
			'fas fa-sort',
			'fas fa-sign-out-alt',
			'fas fa-sign-in-alt',
			'fas fa-share-square',
			'fas fa-share',
			'fas fa-retweet',
			'fas fa-reply-all',
			'fas fa-reply',
			'fas fa-redo-alt',
			'fas fa-redo',
			'fas fa-recycle',
			'fas fa-random',
			'fas fa-play',
			'fas fa-mouse-pointer',
			'fas fa-long-arrow-alt-up',
			'fas fa-long-arrow-alt-right',
			'fas fa-long-arrow-alt-left',
			'fas fa-long-arrow-alt-down',
			'fas fa-location-arrow',
			'fas fa-level-up-alt',
			'fas fa-level-down-alt',
			'fas fa-history',
			'fas fa-hand-pointer',
			'fas fa-hand-point-up',
			'fas fa-hand-point-right',
			'fas fa-hand-point-left',
			'fas fa-hand-point-down',
			'fas fa-external-link-square-alt',
			'fas fa-external-link-alt',
			'fas fa-expand-arrows-alt',
			'fas fa-exchange-alt',
			'fas fa-download',
			'fas fa-cloud-upload-alt',
			'fas fa-cloud-download-alt',
			'fas fa-chevron-up',
			'fas fa-chevron-right',
			'fas fa-chevron-left',
			'fas fa-chevron-down',
			'fas fa-chevron-circle-up',
			'fas fa-chevron-circle-right',
			'fas fa-chevron-circle-left',
			'fas fa-chevron-circle-down',
			'fas fa-chart-line',
			'fas fa-cart-arrow-down',
			'fas fa-caret-up',
			'fas fa-caret-square-up',
			'fas fa-caret-square-right',
			'fas fa-caret-square-left',
			'fas fa-caret-square-down',
			'fas fa-caret-right',
			'fas fa-caret-left',
			'fas fa-caret-down',
			'fas fa-arrows-alt-v',
			'fas fa-arrows-alt-h',
			'fas fa-arrows-alt',
			'fas fa-arrow-up',
			'fas fa-arrow-right',
			'fas fa-arrow-left',
			'fas fa-arrow-down',
			'fas fa-arrow-circle-up',
			'fas fa-arrow-circle-right',
			'fas fa-arrow-circle-left',
			'fas fa-arrow-circle-down',
			'fas fa-arrow-alt-circle-up',
			'fas fa-arrow-alt-circle-right',
			'fas fa-arrow-alt-circle-left',
			'fas fa-arrow-alt-circle-down',
			'fas fa-angle-up',
			'fas fa-angle-right',
			'fas fa-angle-left',
			'fas fa-angle-down',
			'fas fa-angle-double-up',
			'fas fa-angle-double-right',
			'fas fa-angle-double-left',
			'fas fa-angle-double-down',
			'far fa-share-square',
			'far fa-hand-pointer',
			'far fa-hand-point-up',
			'far fa-hand-point-right',
			'far fa-hand-point-left',
			'far fa-hand-point-down',
			'far fa-caret-square-up',
			'far fa-caret-square-right',
			'far fa-caret-square-left',
			'far fa-caret-square-down',
			'far fa-arrow-alt-circle-up',
			'far fa-arrow-alt-circle-right',
			'far fa-arrow-alt-circle-left',
			'far fa-arrow-alt-circle-down',
		],	
		'Audio & Video': [
			'fas fa-volume-up',
			'fas fa-volume-off',
			'fas fa-volume-mute',
			'fas fa-volume-down',
			'fas fa-video',
			'fas fa-undo-alt',
			'fas fa-undo',
			'fas fa-tv',
			'fas fa-sync-alt',
			'fas fa-sync',
			'fas fa-stop-circle',
			'fas fa-stop',
			'fas fa-step-forward',
			'fas fa-step-backward',
			'fas fa-rss-square',
			'fas fa-rss',
			'fas fa-redo-alt',
			'fas fa-redo',
			'fas fa-random',
			'fas fa-podcast',
			'fas fa-play-circle',
			'fas fa-play',
			'fas fa-phone-volume',
			'fas fa-pause-circle',
			'fas fa-pause',
			'fas fa-music',
			'fas fa-microphone-slash',
			'fas fa-microphone-alt-slash',
			'fas fa-microphone-alt',
			'fas fa-microphone',
			'fas fa-headphones',
			'fas fa-forward',
			'fas fa-film',
			'fas fa-file-video',
			'fas fa-file-audio',
			'fas fa-fast-forward',
			'fas fa-fast-backward',
			'fas fa-expand-arrows-alt',
			'fas fa-expand',
			'fas fa-eject',
			'fas fa-compress',
			'fas fa-closed-captioning',
			'fas fa-circle',
			'fas fa-broadcast-tower',
			'fas fa-backward',
			'fas fa-audio-description',
			'fab fa-youtube',
			'far fa-stop-circle',
			'far fa-play-circle',
			'far fa-pause-circle',
			'far fa-file-video',
			'far fa-file-audio',
			'far fa-closed-captioning',
			'far fa-circle',
		],
		'Automotive': [
			'fas fa-truck-pickup',
			'fas fa-truck-monster',
			'fas fa-truck',
			'fas fa-taxi',
			'fas fa-tachometer-alt',
			'fas fa-shuttle-van',
			'fas fa-oil-can',
			'fas fa-motorcycle',
			'fas fa-gas-pump',
			'fas fa-charging-station',
			'fas fa-car-side',
			'fas fa-car-crash',
			'fas fa-car-battery',
			'fas fa-car-alt',
			'fas fa-car',
			'fas fa-bus-alt',
			'fas fa-bus',
			'fas fa-ambulance',
			'fas fa-air-freshener',
		],
		'Autumn': [
			'fas fa-wine-bottle',
			'fas fa-wind',
			'fas fa-tree',
			'fas fa-tractor',
			'fas fa-mountain',
			'fas fa-hiking',
			'fas fa-football-ball',
			'fas fa-drumstick-bite',
			'fas fa-cloud-sun',
			'fas fa-campground',
			'fas fa-apple-alt',
		],
		'Beverage': [
			'fas fa-wine-glass-alt',
			'fas fa-wine-glass',
			'fas fa-wine-bottle',
			'fas fa-glass-martini-alt',
			'fas fa-glass-martini',
			'fas fa-flask',
			'fas fa-coffee',
			'fas fa-cocktail',
			'fas fa-blender',
			'fas fa-beer',
		],
		'Buildings': [
			'fas fa-warehouse',
			'fas fa-vihara',
			'fas fa-university',
			'fas fa-torii-gate',
			'fas fa-synagogue',
			'fas fa-store-alt',
			'fas fa-store',
			'fas fa-school',
			'fas fa-place-of-worship',
			'fas fa-mosque',
			'fas fa-monument',
			'fas fa-landmark',
			'fas fa-kaaba',
			'fas fa-industry',
			'fas fa-house-damage',
			'fas fa-hotel',
			'fas fa-hospital-alt',
			'fas fa-hospital',
			'fas fa-home',
			'fas fa-gopuram',
			'fas fa-dungeon',
			'fas fa-city',
			'fas fa-church',
			'fas fa-campground',
			'fas fa-building',
			'fas fa-archway',
			'far fa-hospital',
			'far fa-building',
		],
		'Business': [
			'fas fa-wallet',
			'fas fa-trademark',
			'fas fa-thumbtack',
			'fas fa-tasks',
			'fas fa-tags',
			'fas fa-tag',
			'fas fa-table',
			'fas fa-stream',
			'fas fa-sticky-note',
			'fas fa-socks',
			'fas fa-sitemap',
			'fas fa-save',
			'fas fa-registered',
			'fas fa-project-diagram',
			'fas fa-print',
			'fas fa-phone-volume',
			'fas fa-phone-square',
			'fas fa-phone-slash',
			'fas fa-phone',
			'fas fa-percent',
			'fas fa-pencil-alt',
			'fas fa-pen-square',
			'fas fa-pen-nib',
			'fas fa-pen-fancy',
			'fas fa-pen-alt',
			'fas fa-pen',
			'fas fa-paste',
			'fas fa-paperclip',
			'fas fa-marker',
			'fas fa-landmark',
			'fas fa-industry',
			'fas fa-highlighter',
			'fas fa-globe',
			'fas fa-glasses',
			'fas fa-folder-plus',
			'fas fa-folder-open',
			'fas fa-folder-minus',
			'fas fa-folder',
			'fas fa-file-alt',
			'fas fa-file',
			'fas fa-fax',
			'fas fa-eraser',
			'fas fa-envelope-square',
			'fas fa-envelope-open',
			'fas fa-envelope',
			'fas fa-edit',
			'fas fa-cut',
			'fas fa-copyright',
			'fas fa-copy',
			'fas fa-compass',
			'fas fa-columns',
			'fas fa-coffee',
			'fas fa-clipboard',
			'fas fa-city',
			'fas fa-chart-pie',
			'fas fa-chart-line',
			'fas fa-chart-bar',
			'fas fa-chart-area',
			'fas fa-certificate',
			'fas fa-calendar-alt',
			'fas fa-calendar',
			'fas fa-calculator',
			'fas fa-business-time',
			'fas fa-bullseye',
			'fas fa-bullhorn',
			'fas fa-building',
			'fas fa-briefcase',
			'fas fa-book',
			'fas fa-birthday-cake',
			'fas fa-balance-scale',
			'fas fa-archive',
			'fas fa-address-card',
			'fas fa-address-book',
			'far fa-sticky-note',
			'far fa-save',
			'far fa-registered',
			'far fa-folder-open',
			'far fa-folder',
			'far fa-file-alt',
			'far fa-file',
			'far fa-envelope-open',
			'far fa-envelope',
			'far fa-edit',
			'far fa-copyright',
			'far fa-copy',
			'far fa-compass',
			'far fa-clipboard',
			'far fa-chart-bar',
			'far fa-calendar-alt',
			'far fa-calendar',
			'far fa-building',
			'far fa-address-card',
			'far fa-address-book',
		],
		'Camping': [
			'fas fa-tree',
			'fas fa-toilet-paper',
			'fas fa-route',
			'fas fa-mountain',
			'fas fa-map-signs',
			'fas fa-map-marked-alt',
			'fas fa-map-marked',
			'fas fa-map',
			'fas fa-hiking',
			'fas fa-frog',
			'fas fa-first-aid',
			'fas fa-fire',
			'fas fa-compass',
			'fas fa-campground',
			'fas fa-binoculars',
			'far fa-map',
			'far fa-compass',
		],
		'Charity': [
			'fas fa-seedling',
			'fas fa-ribbon',
			'fas fa-piggy-bank',
			'fas fa-parachute-box',
			'fas fa-leaf',
			'fas fa-heart',
			'fas fa-handshake',
			'fas fa-hands-helping',
			'fas fa-hand-holding-usd',
			'fas fa-hand-holding-heart',
			'fas fa-globe',
			'fas fa-gift',
			'fas fa-dove',
			'fas fa-donate',
			'fas fa-dollar-sign',
			'far fa-heart',
			'far fa-handshake',
		],
		'Chat': [
			'fas fa-video-slash',
			'fas fa-video',
			'fas fa-smile',
			'fas fa-quote-right',
			'fas fa-quote-left',
			'fas fa-poo',
			'fas fa-phone-slash',
			'fas fa-phone',
			'fas fa-meh',
			'fas fa-frown',
			'fas fa-comments',
			'fas fa-comment-slash',
			'fas fa-comment-dots',
			'fas fa-comment-alt',
			'fas fa-comment',
			'far fa-smile',
			'far fa-meh',
			'far fa-frown',
			'far fa-comments',
			'far fa-comment-dots',
			'far fa-comment-alt',
			'far fa-comment',
		],
		'Chess': [
			'fas fa-square-full',
			'fas fa-chess-rook',
			'fas fa-chess-queen',
			'fas fa-chess-pawn',
			'fas fa-chess-knight',
			'fas fa-chess-king',
			'fas fa-chess-board',
			'fas fa-chess-bishop',
			'fas fa-chess',
		],
		'Childhood': [
			'fas fa-shapes',
			'fas fa-school',
			'fas fa-robot',
			'fas fa-gamepad',
			'fas fa-cookie-bite',
			'fas fa-cookie',
			'fas fa-birthday-cake',
			'fas fa-bath',
			'fas fa-apple-alt',
		],
		'Clothing': [
			'fas fa-user-tie',
			'fas fa-tshirt',
			'fas fa-socks',
			'fas fa-shoe-prints',
			'fas fa-hat-wizard',
			'fas fa-graduation-cap',
		],
		'Code': [
			'fas fa-window-restore',
			'fas fa-window-minimize',
			'fas fa-window-maximize',
			'fas fa-window-close',
			'fas fa-user-secret',
			'fas fa-terminal',
			'fas fa-stream',
			'fas fa-sitemap',
			'fas fa-shield-alt',
			'fas fa-qrcode',
			'fas fa-project-diagram',
			'fas fa-microchip',
			'fas fa-laptop-code',
			'fas fa-keyboard',
			'fas fa-folder-open',
			'fas fa-folder',
			'fas fa-fire-extinguisher',
			'fas fa-filter',
			'fas fa-file-code',
			'fas fa-file-alt',
			'fas fa-file',
			'fas fa-coffee',
			'fas fa-code-branch',
			'fas fa-code',
			'fas fa-bug',
			'fas fa-bath',
			'fas fa-barcode',
			'fas fa-archive',
			'far fa-window-restore',
			'far fa-window-minimize',
			'far fa-window-maximize',
			'far fa-window-close',
			'far fa-keyboard',
			'far fa-folder-open',
			'far fa-folder',
			'far fa-file-code',
			'far fa-file-alt',
			'far fa-file',
		],
		'Communication': [
			'fas fa-wifi',
			'fas fa-tty',
			'fas fa-rss-square',
			'fas fa-rss',
			'fas fa-phone-volume',
			'fas fa-phone-square',
			'fas fa-phone-slash',
			'fas fa-phone',
			'fas fa-paper-plane',
			'fas fa-mobile-alt',
			'fas fa-mobile',
			'fas fa-microphone-slash',
			'fas fa-microphone-alt-slash',
			'fas fa-microphone-alt',
			'fas fa-microphone',
			'fas fa-language',
			'fas fa-inbox',
			'fas fa-fax',
			'fas fa-envelope-square',
			'fas fa-envelope-open',
			'fas fa-envelope',
			'fas fa-comments',
			'fas fa-comment-alt',
			'fas fa-comment',
			'fas fa-chalkboard',
			'fas fa-bullhorn',
			'fas fa-broadcast-tower',
			'fas fa-bell-slash',
			'fas fa-bell',
			'fas fa-at',
			'fas fa-assistive-listening-systems',
			'fas fa-american-sign-language-interpreting',
			'fas fa-address-card',
			'fas fa-address-book',
			'fab fa-bluetooth-b',
			'fab fa-bluetooth',
			'far fa-paper-plane',
			'far fa-envelope-open',
			'far fa-envelope',
			'far fa-comments',
			'far fa-comment-alt',
			'far fa-comment',
			'far fa-bell-slash',
			'far fa-bell',
			'far fa-address-card',
			'far fa-address-book',
		],
		'Computers': [
			'fas fa-upload',
			'fas fa-tv',
			'fas fa-tablet-alt',
			'fas fa-tablet',
			'fas fa-stream',
			'fas fa-save',
			'fas fa-print',
			'fas fa-power-off',
			'fas fa-plug',
			'fas fa-mobile-alt',
			'fas fa-mobile',
			'fas fa-microchip',
			'fas fa-memory',
			'fas fa-laptop',
			'fas fa-keyboard',
			'fas fa-headphones',
			'fas fa-hdd',
			'fas fa-download',
			'fas fa-desktop',
			'fas fa-database',
			'fas fa-compact-disc',
			'far fa-save',
			'far fa-keyboard',
			'far fa-hdd',
		],
		'Construction': [
			'fas fa-wrench',
			'fas fa-truck-pickup',
			'fas fa-toolbox',
			'fas fa-screwdriver',
			'fas fa-ruler-vertical',
			'fas fa-ruler-horizontal',
			'fas fa-ruler-combined',
			'fas fa-ruler',
			'fas fa-pencil-ruler',
			'fas fa-pencil-alt',
			'fas fa-paint-roller',
			'fas fa-hammer',
			'fas fa-drafting-compass',
			'fas fa-brush',
		],
		'Currency': [
			'fas fa-yen-sign',
			'fas fa-won-sign',
			'fas fa-shekel-sign',
			'fas fa-rupee-sign',
			'fas fa-ruble-sign',
			'fas fa-pound-sign',
			'fas fa-money-check-alt',
			'fas fa-money-check',
			'fas fa-money-bill-wave-alt',
			'fas fa-money-bill-wave',
			'fas fa-money-bill-alt',
			'fas fa-money-bill',
			'fas fa-lira-sign',
			'fas fa-hryvnia',
			'fas fa-euro-sign',
			'fas fa-dollar-sign',
			'fab fa-gg-circle',
			'fab fa-gg',
			'fab fa-ethereum',
			'fab fa-btc',
			'fab fa-bitcoin',
			'far fa-money-bill-alt',
		],
		'Date & Time': [
			'fas fa-stopwatch',
			'fas fa-hourglass-start',
			'fas fa-hourglass-half',
			'fas fa-hourglass-end',
			'fas fa-hourglass',
			'fas fa-clock',
			'fas fa-calendar-times',
			'fas fa-calendar-plus',
			'fas fa-calendar-minus',
			'fas fa-calendar-check',
			'fas fa-calendar-alt',
			'fas fa-calendar',
			'fas fa-bell-slash',
			'fas fa-bell',
			'far fa-hourglass',
			'far fa-clock',
			'far fa-calendar-times',
			'far fa-calendar-plus',
			'far fa-calendar-minus',
			'far fa-calendar-check',
			'far fa-calendar-alt',
			'far fa-calendar',
			'far fa-bell-slash',
			'far fa-bell',
		],
		'Design': [
			'fas fa-vector-square',
			'fas fa-tint-slash',
			'fas fa-tint',
			'fas fa-swatchbook',
			'fas fa-stamp',
			'fas fa-spray-can',
			'fas fa-splotch',
			'fas fa-save',
			'fas fa-ruler-vertical',
			'fas fa-ruler-horizontal',
			'fas fa-ruler-combined',
			'fas fa-pencil-ruler',
			'fas fa-pencil-alt',
			'fas fa-pen-nib',
			'fas fa-pen-fancy',
			'fas fa-pen-alt',
			'fas fa-pen',
			'fas fa-paste',
			'fas fa-palette',
			'fas fa-paint-roller',
			'fas fa-paint-brush',
			'fas fa-object-ungroup',
			'fas fa-object-group',
			'fas fa-marker',
			'fas fa-magic',
			'fas fa-layer-group',
			'fas fa-highlighter',
			'fas fa-fill-drip',
			'fas fa-fill',
			'fas fa-eye-slash',
			'fas fa-eye-dropper',
			'fas fa-eye',
			'fas fa-eraser',
			'fas fa-edit',
			'fas fa-draw-polygon',
			'fas fa-drafting-compass',
			'fas fa-cut',
			'fas fa-crosshairs',
			'fas fa-crop-alt',
			'fas fa-crop',
			'fas fa-copy',
			'fas fa-clone',
			'fas fa-brush',
			'fas fa-bezier-curve',
			'fas fa-adjust',
			'far fa-save',
			'far fa-object-ungroup',
			'far fa-object-group',
			'far fa-eye-slash',
			'far fa-eye',
			'far fa-edit',
			'far fa-copy',
			'far fa-clone',
		],
		'Editors': [
			'fas fa-wrench',
			'fas fa-unlink',
			'fas fa-undo-alt',
			'fas fa-undo',
			'fas fa-underline',
			'fas fa-trash-alt',
			'fas fa-trash',
			'fas fa-th-list',
			'fas fa-th-large',
			'fas fa-th',
			'fas fa-text-width',
			'fas fa-text-height',
			'fas fa-tasks',
			'fas fa-table',
			'fas fa-sync-alt',
			'fas fa-sync',
			'fas fa-superscript',
			'fas fa-subscript',
			'fas fa-strikethrough',
			'fas fa-share',
			'fas fa-screwdriver',
			'fas fa-reply-all',
			'fas fa-reply',
			'fas fa-redo-alt',
			'fas fa-redo',
			'fas fa-quote-right',
			'fas fa-quote-left',
			'fas fa-print',
			'fas fa-pencil-alt',
			'fas fa-pen-nib',
			'fas fa-pen-fancy',
			'fas fa-pen-alt',
			'fas fa-pen',
			'fas fa-paste',
			'fas fa-paragraph',
			'fas fa-paperclip',
			'fas fa-paper-plane',
			'fas fa-outdent',
			'fas fa-marker',
			'fas fa-list-ul',
			'fas fa-list-ol',
			'fas fa-list-alt',
			'fas fa-list',
			'fas fa-link',
			'fas fa-italic',
			'fas fa-indent',
			'fas fa-i-cursor',
			'fas fa-highlighter',
			'fas fa-heading',
			'fas fa-glasses',
			'fas fa-font',
			'fas fa-file-alt',
			'fas fa-file',
			'fas fa-eraser',
			'fas fa-edit',
			'fas fa-cut',
			'fas fa-copy',
			'fas fa-columns',
			'fas fa-clone',
			'fas fa-clipboard',
			'fas fa-bold',
			'fas fa-align-right',
			'fas fa-align-left',
			'fas fa-align-justify',
			'fas fa-align-center',
			'far fa-trash-alt',
			'far fa-list-alt',
			'far fa-file-alt',
			'far fa-file',
			'far fa-edit',
			'far fa-copy',
			'far fa-clone',
			'far fa-clipboard',
		],
		'Education': [
			'fas fa-user-graduate',
			'fas fa-theater-masks',
			'fas fa-shapes',
			'fas fa-school',
			'fas fa-music',
			'fas fa-microscope',
			'fas fa-laptop-code',
			'fas fa-graduation-cap',
			'fas fa-chalkboard-teacher',
			'fas fa-chalkboard',
			'fas fa-book-reader',
			'fas fa-book-open',
			'fas fa-bell-slash',
			'fas fa-bell',
			'fas fa-award',
			'fas fa-atom',
			'fas fa-apple-alt',
			'far fa-bell-slash',
			'far fa-bell',
		],
		'Emoji': [
			'fas fa-tired',
			'fas fa-surprise',
			'fas fa-smile-wink',
			'fas fa-smile-beam',
			'fas fa-smile',
			'fas fa-sad-tear',
			'fas fa-sad-cry',
			'fas fa-meh-rolling-eyes',
			'fas fa-meh-blank',
			'fas fa-meh',
			'fas fa-laugh-wink',
			'fas fa-laugh-squint',
			'fas fa-laugh-beam',
			'fas fa-laugh',
			'fas fa-kiss-wink-heart',
			'fas fa-kiss-beam',
			'fas fa-kiss',
			'fas fa-grin-wink',
			'fas fa-grin-tongue-wink',
			'fas fa-grin-tongue-squint',
			'fas fa-grin-tongue',
			'fas fa-grin-tears',
			'fas fa-grin-stars',
			'fas fa-grin-squint-tears',
			'fas fa-grin-squint',
			'fas fa-grin-hearts',
			'fas fa-grin-beam-sweat',
			'fas fa-grin-beam',
			'fas fa-grin-alt',
			'fas fa-grin',
			'fas fa-grimace',
			'fas fa-frown-open',
			'fas fa-frown',
			'fas fa-flushed',
			'fas fa-dizzy',
			'fas fa-angry',
			'far fa-tired',
			'far fa-surprise',
			'far fa-smile-wink',
			'far fa-smile-beam',
			'far fa-smile',
			'far fa-sad-tear',
			'far fa-sad-cry',
			'far fa-meh-rolling-eyes',
			'far fa-meh-blank',
			'far fa-meh',
			'far fa-laugh-wink',
			'far fa-laugh-squint',
			'far fa-laugh-beam',
			'far fa-laugh',
			'far fa-kiss-wink-heart',
			'far fa-kiss-beam',
			'far fa-kiss',
			'far fa-grin-wink',
			'far fa-grin-tongue-wink',
			'far fa-grin-tongue-squint',
			'far fa-grin-tongue',
			'far fa-grin-tears',
			'far fa-grin-stars',
			'far fa-grin-squint-tears',
			'far fa-grin-squint',
			'far fa-grin-hearts',
			'far fa-grin-beam-sweat',
			'far fa-grin-beam',
			'far fa-grin-alt',
			'far fa-grin',
			'far fa-grimace',
			'far fa-frown-open',
			'far fa-frown',
			'far fa-flushed',
			'far fa-dizzy',
			'far fa-angry',
		],
		'Energy': [
			'fas fa-wind',
			'fas fa-sun',
			'fas fa-solar-panel',
			'fas fa-seedling',
			'fas fa-power-off',
			'fas fa-poop',
			'fas fa-plug',
			'fas fa-lightbulb',
			'fas fa-leaf',
			'fas fa-industry',
			'fas fa-gas-pump',
			'fas fa-fire',
			'fas fa-charging-station',
			'fas fa-burn',
			'fas fa-broadcast-tower',
			'fas fa-battery-three-quarters',
			'fas fa-battery-quarter',
			'fas fa-battery-half',
			'fas fa-battery-full',
			'fas fa-battery-empty',
			'fas fa-atom',
			'far fa-sun',
			'far fa-lightbulb',
		],
		'Files': [
			'fas fa-sticky-note',
			'fas fa-save',
			'fas fa-paste',
			'fas fa-folder-open',
			'fas fa-folder',
			'fas fa-file-word',
			'fas fa-file-video',
			'fas fa-file-powerpoint',
			'fas fa-file-pdf',
			'fas fa-file-image',
			'fas fa-file-excel',
			'fas fa-file-code',
			'fas fa-file-audio',
			'fas fa-file-archive',
			'fas fa-file-alt',
			'fas fa-file',
			'fas fa-cut',
			'fas fa-copy',
			'fas fa-clone',
			'fas fa-archive',
			'far fa-sticky-note',
			'far fa-save',
			'far fa-folder-open',
			'far fa-folder',
			'far fa-file-word',
			'far fa-file-video',
			'far fa-file-powerpoint',
			'far fa-file-pdf',
			'far fa-file-image',
			'far fa-file-excel',
			'far fa-file-code',
			'far fa-file-audio',
			'far fa-file-archive',
			'far fa-file-alt',
			'far fa-file',
			'far fa-copy',
			'far fa-clone',
		],
		'Finance': [
			'fas fa-wallet',
			'fas fa-stamp',
			'fas fa-receipt',
			'fas fa-piggy-bank',
			'fas fa-percentage',
			'fas fa-money-check-alt',
			'fas fa-money-check',
			'fas fa-money-bill-wave-alt',
			'fas fa-money-bill-wave',
			'fas fa-money-bill-alt',
			'fas fa-money-bill',
			'fas fa-landmark',
			'fas fa-hand-holding-usd',
			'fas fa-file-invoice-dollar',
			'fas fa-file-invoice',
			'fas fa-donate',
			'fas fa-credit-card',
			'fas fa-comments-dollar',
			'fas fa-comment-dollar',
			'fas fa-coins',
			'fas fa-chart-pie',
			'fas fa-chart-line',
			'fas fa-book',
			'fas fa-balance-scale',
			'far fa-money-bill-alt',
			'far fa-credit-card',
		],	
		'Fitness': [
			'fas fa-walking',
			'fas fa-swimmer',
			'fas fa-spa',
			'fas fa-shoe-prints',
			'fas fa-running',
			'fas fa-hiking',
			'fas fa-heartbeat',
			'fas fa-heart',
			'fas fa-burn',
			'fas fa-bicycle',
			'far fa-heart',
		],
		'Food': [	
			'fas fa-stroopwafel',
			'fas fa-seedling',
			'fas fa-pepper-hot',
			'fas fa-lemon',
			'fas fa-fish',
			'fas fa-drumstick-bite',
			'fas fa-cookie',
			'fas fa-bone',
			'fas fa-apple-alt',
			'far fa-lemon',
		],
		'Fruits & Vegetables': [	
			'fas fa-seedling',
			'fas fa-lemon',
			'fas fa-leaf',
			'fas fa-apple-alt',
			'far fa-lemon',
		],	
		'Games': [
			'fas fa-puzzle-piece',
			'fas fa-heart',
			'fas fa-headset',
			'fas fa-ghost',
			'fas fa-gamepad',
			'fas fa-dice-two',
			'fas fa-dice-three',
			'fas fa-dice-six',
			'fas fa-dice-one',
			'fas fa-dice-four',
			'fas fa-dice-five',
			'fas fa-dice-d6',
			'fas fa-dice-d20',
			'fas fa-dice',
			'fas fa-chess-rook',
			'fas fa-chess-queen',
			'fas fa-chess-pawn',
			'fas fa-chess-knight',
			'fas fa-chess-king',
			'fas fa-chess-board',
			'fas fa-chess-bishop',
			'fas fa-chess',
			'fab fa-xbox',
			'fab fa-twitch',
			'fab fa-steam-symbol',
			'fab fa-steam-square',
			'fab fa-steam',
			'fab fa-playstation',
			'far fa-heart',
		],
		'Tabletop Gaming': [
			'fas fa-skull-crossbones',
			'fas fa-scroll',
			'fas fa-ring',
			'fas fa-hat-wizard',
			'fas fa-fist-raised',
			'fas fa-dungeon',
			'fas fa-dragon',
			'fas fa-dice-d6',
			'fas fa-dice-d20',
			'fas fa-book-dead',
			'fab fa-wizards-of-the-coast',
			'fab fa-penny-arcade',
			'fab fa-fantasy-flight-games',
			'fab fa-d-and-d-beyond',
			'fab fa-d-and-d',
			'fab fa-critical-role',
			'fab fa-acquisitions-incorporated',
		],	
		'Genders': [
			'fas fa-venus-mars',
			'fas fa-venus-double',
			'fas fa-venus',
			'fas fa-transgender-alt',
			'fas fa-transgender',
			'fas fa-neuter',
			'fas fa-mercury',
			'fas fa-mars-stroke-v',
			'fas fa-mars-stroke-h',
			'fas fa-mars-stroke',
			'fas fa-mars-double',
			'fas fa-mars',
			'fas fa-genderless',
		],
		'Halloween': [	
			'fas fa-toilet-paper',
			'fas fa-spider',
			'fas fa-skull-crossbones',
			'fas fa-mask',
			'fas fa-hat-wizard',
			'fas fa-ghost',
			'fas fa-crow',
			'fas fa-cloud-moon',
			'fas fa-cat',
			'fas fa-broom',
			'fas fa-book-dead',
		],	
		'Hands': [	
			'fas fa-thumbs-up',
			'fas fa-thumbs-down',
			'fas fa-praying-hands',
			'fas fa-handshake',
			'fas fa-hands-helping',
			'fas fa-hands',
			'fas fa-hand-spock',
			'fas fa-hand-scissors',
			'fas fa-hand-rock',
			'fas fa-hand-pointer',
			'fas fa-hand-point-up',
			'fas fa-hand-point-right',
			'fas fa-hand-point-left',
			'fas fa-hand-point-down',
			'fas fa-hand-peace',
			'fas fa-hand-paper',
			'fas fa-hand-lizard',
			'fas fa-hand-holding-usd',
			'fas fa-hand-holding-heart',
			'fas fa-hand-holding',
			'fas fa-fist-raised',
			'fas fa-allergies',
			'far fa-thumbs-up',
			'far fa-thumbs-down',
			'far fa-handshake',
			'far fa-hand-spock',
			'far fa-hand-scissors',
			'far fa-hand-rock',
			'far fa-hand-pointer',
			'far fa-hand-point-up',
			'far fa-hand-point-right',
			'far fa-hand-point-left',
			'far fa-hand-point-down',
			'far fa-hand-peace',
			'far fa-hand-paper',
			'far fa-hand-lizard',
		],	
		'Health': [	
			'fas fa-wheelchair',
			'fas fa-user-md',
			'fas fa-stethoscope',
			'fas fa-prescription',
			'fas fa-plus-square',
			'fas fa-medkit',
			'fas fa-hospital',
			'fas fa-heartbeat',
			'fas fa-heart',
			'fas fa-h-square',
			'fas fa-ambulance',
			'fab fa-accessible-icon',
			'far fa-plus-square',
			'far fa-hospital',
			'far fa-heart',
		],
		'Holiday': [		
			'fas fa-gift',
			'fas fa-cookie-bite',
		],	
		'Hotel': [
			'fas fa-wifi',
			'fas fa-wheelchair',
			'fas fa-utensils',
			'fas fa-umbrella-beach',
			'fas fa-tv',
			'fas fa-swimming-pool',
			'fas fa-swimmer',
			'fas fa-suitcase-rolling',
			'fas fa-suitcase',
			'fas fa-spa',
			'fas fa-snowflake',
			'fas fa-smoking-ban',
			'fas fa-smoking',
			'fas fa-shuttle-van',
			'fas fa-shower',
			'fas fa-luggage-cart',
			'fas fa-key',
			'fas fa-infinity',
			'fas fa-hotel',
			'fas fa-hot-tub',
			'fas fa-glass-martini-alt',
			'fas fa-glass-martini',
			'fas fa-dumbbell',
			'fas fa-door-open',
			'fas fa-door-closed',
			'fas fa-dice-five',
			'fas fa-dice',
			'fas fa-concierge-bell',
			'fas fa-coffee',
			'fas fa-cocktail',
			'fas fa-car',
			'fas fa-briefcase',
			'fas fa-bed',
			'fas fa-bath',
			'far fa-snowflake',
		],	
		'Household': [
			'fas fa-tv',
			'fas fa-toilet-paper',
			'fas fa-snowflake',
			'fas fa-shower',
			'fas fa-plug',
			'fas fa-lightbulb',
			'fas fa-dungeon',
			'fas fa-door-open',
			'fas fa-door-closed',
			'fas fa-couch',
			'fas fa-coffee',
			'fas fa-chair',
			'fas fa-blender',
			'fas fa-bell',
			'fas fa-bed',
			'fas fa-bath',
			'far fa-snowflake',
			'far fa-lightbulb',
			'far fa-bell',
		],	
		'Images': [
			'fas fa-tint',
			'fas fa-sliders-h',
			'fas fa-portrait',
			'fas fa-images',
			'fas fa-image',
			'fas fa-id-card',
			'fas fa-id-badge',
			'fas fa-film',
			'fas fa-file-image',
			'fas fa-eye-slash',
			'fas fa-eye-dropper',
			'fas fa-eye',
			'fas fa-expand',
			'fas fa-compress',
			'fas fa-clone',
			'fas fa-chalkboard',
			'fas fa-camera-retro',
			'fas fa-camera',
			'fas fa-bolt',
			'fas fa-adjust',
			'far fa-images',
			'far fa-image',
			'far fa-id-card',
			'far fa-id-badge',
			'far fa-file-image',
			'far fa-eye-slash',
			'far fa-eye',
			'far fa-clone',
		],	
		'Interfaces': [
			'fas fa-wrench',
			'fas fa-wifi',
			'fas fa-volume-up',
			'fas fa-volume-off',
			'fas fa-volume-mute',
			'fas fa-volume-down',
			'fas fa-user-circle',
			'fas fa-user-alt',
			'fas fa-user',
			'fas fa-upload',
			'fas fa-undo-alt',
			'fas fa-undo',
			'fas fa-trophy',
			'fas fa-trash-alt',
			'fas fa-trash',
			'fas fa-toggle-on',
			'fas fa-toggle-off',
			'fas fa-times-circle',
			'fas fa-times',
			'fas fa-thumbs-up',
			'fas fa-thumbs-down',
			'fas fa-sync-alt',
			'fas fa-sync',
			'fas fa-star-half',
			'fas fa-star',
			'fas fa-sort-up',
			'fas fa-sort-numeric-up',
			'fas fa-sort-numeric-down',
			'fas fa-sort-down',
			'fas fa-sort-amount-up',
			'fas fa-sort-amount-down',
			'fas fa-sort-alpha-up',
			'fas fa-sort-alpha-down',
			'fas fa-sort',
			'fas fa-smile',
			'fas fa-sliders-h',
			'fas fa-sitemap',
			'fas fa-signal',
			'fas fa-sign-out-alt',
			'fas fa-sign-in-alt',
			'fas fa-shield-alt',
			'fas fa-share-square',
			'fas fa-share-alt-square',
			'fas fa-share-alt',
			'fas fa-share',
			'fas fa-search-plus',
			'fas fa-search-minus',
			'fas fa-search',
			'fas fa-screwdriver',
			'fas fa-save',
			'fas fa-rss-square',
			'fas fa-rss',
			'fas fa-reply-all',
			'fas fa-reply',
			'fas fa-redo-alt',
			'fas fa-redo',
			'fas fa-quote-right',
			'fas fa-quote-left',
			'fas fa-question-circle',
			'fas fa-question',
			'fas fa-qrcode',
			'fas fa-poo',
			'fas fa-plus-square',
			'fas fa-plus-circle',
			'fas fa-plus',
			'fas fa-pencil-alt',
			'fas fa-pen-fancy',
			'fas fa-pen-alt',
			'fas fa-pen',
			'fas fa-paste',
			'fas fa-minus-square',
			'fas fa-minus-circle',
			'fas fa-minus',
			'fas fa-microphone-slash',
			'fas fa-microphone-alt',
			'fas fa-microphone',
			'fas fa-meh',
			'fas fa-medal',
			'fas fa-marker',
			'fas fa-magic',
			'fas fa-language',
			'fas fa-info-circle',
			'fas fa-info',
			'fas fa-i-cursor',
			'fas fa-home',
			'fas fa-history',
			'fas fa-heart',
			'fas fa-hashtag',
			'fas fa-grip-vertical',
			'fas fa-grip-horizontal',
			'fas fa-glasses',
			'fas fa-frown',
			'fas fa-folder-open',
			'fas fa-folder',
			'fas fa-flag-checkered',
			'fas fa-flag',
			'fas fa-fingerprint',
			'fas fa-filter',
			'fas fa-file-upload',
			'fas fa-file-import',
			'fas fa-file-export',
			'fas fa-file-download',
			'fas fa-file-alt',
			'fas fa-file',
			'fas fa-eye-slash',
			'fas fa-eye',
			'fas fa-external-link-square-alt',
			'fas fa-external-link-alt',
			'fas fa-exclamation-triangle',
			'fas fa-exclamation-circle',
			'fas fa-exclamation',
			'fas fa-eraser',
			'fas fa-envelope-open',
			'fas fa-envelope',
			'fas fa-ellipsis-v',
			'fas fa-ellipsis-h',
			'fas fa-edit',
			'fas fa-download',
			'fas fa-dot-circle',
			'fas fa-database',
			'fas fa-cut',
			'fas fa-copy',
			'fas fa-cogs',
			'fas fa-cog',
			'fas fa-coffee',
			'fas fa-cloud-upload-alt',
			'fas fa-cloud-download-alt',
			'fas fa-cloud',
			'fas fa-clone',
			'fas fa-clipboard',
			'fas fa-circle',
			'fas fa-check-square',
			'fas fa-check-double',
			'fas fa-check-circle',
			'fas fa-check',
			'fas fa-certificate',
			'fas fa-calendar-times',
			'fas fa-calendar-plus',
			'fas fa-calendar-minus',
			'fas fa-calendar-check',
			'fas fa-calendar-alt',
			'fas fa-calendar',
			'fas fa-calculator',
			'fas fa-bullseye',
			'fas fa-bullhorn',
			'fas fa-bug',
			'fas fa-bell-slash',
			'fas fa-bell',
			'fas fa-beer',
			'fas fa-bars',
			'fas fa-barcode',
			'fas fa-ban',
			'fas fa-award',
			'far fa-user-circle',
			'far fa-user',
			'far fa-trash-alt',
			'far fa-times-circle',
			'far fa-thumbs-up',
			'far fa-thumbs-down',
			'far fa-star-half',
			'far fa-star',
			'far fa-smile',
			'far fa-share-square',
			'far fa-save',
			'far fa-question-circle',
			'far fa-plus-square',
			'far fa-minus-square',
			'far fa-meh',
			'far fa-heart',
			'far fa-frown',
			'far fa-folder-open',
			'far fa-folder',
			'far fa-flag',
			'far fa-file-alt',
			'far fa-file',
			'far fa-eye-slash',
			'far fa-eye',
			'far fa-envelope-open',
			'far fa-envelope',
			'far fa-edit',
			'far fa-dot-circle',
			'far fa-copy',
			'far fa-clone',
			'far fa-clipboard',
			'far fa-circle',
			'far fa-check-square',
			'far fa-check-circle',
			'far fa-calendar-times',
			'far fa-calendar-plus',
			'far fa-calendar-minus',
			'far fa-calendar-check',
			'far fa-calendar-alt',
			'far fa-calendar',
			'far fa-bell-slash',
			'far fa-bell',
		],
		'Logistics': [
			'fas fa-warehouse',
			'fas fa-truck',
			'fas fa-shipping-fast',
			'fas fa-pallet',
			'fas fa-dolly-flatbed',
			'fas fa-dolly',
			'fas fa-clipboard-list',
			'fas fa-clipboard-check',
			'fas fa-boxes',
			'fas fa-box',
		],	
		'Maps': [
			'fas fa-wrench',
			'fas fa-wine-glass',
			'fas fa-wifi',
			'fas fa-wheelchair',
			'fas fa-utensils',
			'fas fa-utensil-spoon',
			'fas fa-university',
			'fas fa-umbrella',
			'fas fa-tty',
			'fas fa-truck',
			'fas fa-trophy',
			'fas fa-tree',
			'fas fa-train',
			'fas fa-traffic-light',
			'fas fa-tint',
			'fas fa-ticket-alt',
			'fas fa-thumbtack',
			'fas fa-taxi',
			'fas fa-tags',
			'fas fa-tag',
			'fas fa-suitcase',
			'fas fa-subway',
			'fas fa-street-view',
			'fas fa-shower',
			'fas fa-shopping-cart',
			'fas fa-shopping-basket',
			'fas fa-shopping-bag',
			'fas fa-shoe-prints',
			'fas fa-ship',
			'fas fa-search-plus',
			'fas fa-search-minus',
			'fas fa-search',
			'fas fa-route',
			'fas fa-rocket',
			'fas fa-road',
			'fas fa-recycle',
			'fas fa-print',
			'fas fa-plus-square',
			'fas fa-plus',
			'fas fa-plug',
			'fas fa-plane',
			'fas fa-phone-volume',
			'fas fa-phone-square',
			'fas fa-phone',
			'fas fa-paw',
			'fas fa-parking',
			'fas fa-newspaper',
			'fas fa-music',
			'fas fa-motorcycle',
			'fas fa-money-bill-alt',
			'fas fa-money-bill',
			'fas fa-medkit',
			'fas fa-map-signs',
			'fas fa-map-pin',
			'fas fa-map-marker-alt',
			'fas fa-map-marker',
			'fas fa-map',
			'fas fa-male',
			'fas fa-magnet',
			'fas fa-low-vision',
			'fas fa-location-arrow',
			'fas fa-lightbulb',
			'fas fa-life-ring',
			'fas fa-lemon',
			'fas fa-leaf',
			'fas fa-layer-group',
			'fas fa-landmark',
			'fas fa-key',
			'fas fa-info-circle',
			'fas fa-info',
			'fas fa-industry',
			'fas fa-images',
			'fas fa-image',
			'fas fa-hospital',
			'fas fa-home',
			'fas fa-helicopter',
			'fas fa-heartbeat',
			'fas fa-heart',
			'fas fa-h-square',
			'fas fa-graduation-cap',
			'fas fa-globe',
			'fas fa-glass-martini',
			'fas fa-gift',
			'fas fa-gavel',
			'fas fa-gamepad',
			'fas fa-flask',
			'fas fa-flag-checkered',
			'fas fa-flag',
			'fas fa-fire-extinguisher',
			'fas fa-fire',
			'fas fa-fighter-jet',
			'fas fa-eye-slash',
			'fas fa-eye',
			'fas fa-draw-polygon',
			'fas fa-dollar-sign',
			'fas fa-directions',
			'fas fa-crosshairs',
			'fas fa-coffee',
			'fas fa-car',
			'fas fa-building',
			'fas fa-briefcase',
			'fas fa-bookmark',
			'fas fa-book',
			'fas fa-bomb',
			'fas fa-blind',
			'fas fa-birthday-cake',
			'fas fa-binoculars',
			'fas fa-bicycle',
			'fas fa-bell-slash',
			'fas fa-bell',
			'fas fa-beer',
			'fas fa-bed',
			'fas fa-bath',
			'fas fa-balance-scale',
			'fas fa-anchor',
			'fas fa-ambulance',
			'far fa-plus-square',
			'far fa-newspaper',
			'far fa-money-bill-alt',
			'far fa-map',
			'far fa-lightbulb',
			'far fa-life-ring',
			'far fa-lemon',
			'far fa-images',
			'far fa-image',
			'far fa-hospital',
			'far fa-heart',
			'far fa-flag',
			'far fa-eye-slash',
			'far fa-eye',
			'far fa-building',
			'far fa-bookmark',
			'far fa-bell-slash',
			'far fa-bell',
		],
		'Maritime': [	
			'fas fa-wind',
			'fas fa-swimmer',
			'fas fa-skull-crossbones',
			'fas fa-ship',
			'fas fa-frog',
			'fas fa-dharmachakra',
			'fas fa-compass',
			'fas fa-binoculars',
			'fas fa-anchor',
			'far fa-compass',
		],	
		'Marketing': [
			'fas fa-search-location',
			'fas fa-search-dollar',
			'fas fa-poll-h',
			'fas fa-poll',
			'fas fa-mail-bulk',
			'fas fa-lightbulb',
			'fas fa-funnel-dollar',
			'fas fa-envelope-open-text',
			'fas fa-comments-dollar',
			'fas fa-comment-dollar',
			'fas fa-bullseye',
			'fas fa-bullhorn',
			'fas fa-ad',
			'far fa-lightbulb',
		],
		'Mathematics': [	
			'fas fa-times',
			'fas fa-superscript',
			'fas fa-subscript',
			'fas fa-square-root-alt',
			'fas fa-plus',
			'fas fa-percentage',
			'fas fa-not-equal',
			'fas fa-minus',
			'fas fa-less-than-equal',
			'fas fa-less-than',
			'fas fa-infinity',
			'fas fa-greater-than-equal',
			'fas fa-greater-than',
			'fas fa-equals',
			'fas fa-divide',
			'fas fa-calculator',
		],	
		'Medical': [
			'fas fa-x-ray',
			'fas fa-weight',
			'fas fa-vials',
			'fas fa-vial',
			'fas fa-user-md',
			'fas fa-tooth',
			'fas fa-thermometer',
			'fas fa-teeth-open',
			'fas fa-teeth',
			'fas fa-tablets',
			'fas fa-syringe',
			'fas fa-stethoscope',
			'fas fa-star-of-life',
			'fas fa-smoking-ban',
			'fas fa-smoking',
			'fas fa-procedures',
			'fas fa-prescription-bottle-alt',
			'fas fa-prescription-bottle',
			'fas fa-prescription',
			'fas fa-poop',
			'fas fa-plus',
			'fas fa-pills',
			'fas fa-notes-medical',
			'fas fa-mortar-pestle',
			'fas fa-microscope',
			'fas fa-joint',
			'fas fa-id-card-alt',
			'fas fa-hospital-symbol',
			'fas fa-hospital-alt',
			'fas fa-hospital',
			'fas fa-heartbeat',
			'fas fa-heart',
			'fas fa-head-side-mask',
			'fas fa-first-aid',
			'fas fa-file-prescription',
			'fas fa-file-medical-alt',
			'fas fa-file-medical',
			'fas fa-dna',
			'fas fa-diagnoses',
			'fas fa-capsules',
			'fas fa-cannabis',
			'fas fa-burn',
			'fas fa-briefcase-medical',
			'fas fa-brain',
			'fas fa-bong',
			'fas fa-bone',
			'fas fa-band-aid',
			'fas fa-ambulance',
			'fas fa-allergies',
			'far fa-hospital',
			'far fa-heart',
		],	
		'Moving': [
			'fas fa-wine-glass',
			'fas fa-truck-moving',
			'fas fa-truck-loading',
			'fas fa-tape',
			'fas fa-suitcase',
			'fas fa-sign',
			'fas fa-route',
			'fas fa-people-carry',
			'fas fa-dolly',
			'fas fa-couch',
			'fas fa-box-open',
			'fas fa-archive',
		],	
		'Music': [
			'fas fa-volume-up',
			'fas fa-volume-off',
			'fas fa-volume-mute',
			'fas fa-volume-down',
			'fas fa-sliders-h',
			'fas fa-play',
			'fas fa-music',
			'fas fa-microphone-slash',
			'fas fa-microphone-alt-slash',
			'fas fa-microphone-alt',
			'fas fa-microphone',
			'fas fa-headphones-alt',
			'fas fa-headphones',
			'fas fa-file-audio',
			'fas fa-drum-steelpan',
			'fas fa-drum',
			'fas fa-compact-disc',
			'fab fa-spotify',
			'fab fa-soundcloud',
			'fab fa-napster',
			'far fa-file-audio',
		],
		'Objects': [
			'fas fa-wrench',
			'fas fa-wine-glass',
			'fas fa-wheelchair',
			'fas fa-weight',
			'fas fa-wallet',
			'fas fa-utensils',
			'fas fa-utensil-spoon',
			'fas fa-unlock-alt',
			'fas fa-unlock',
			'fas fa-university',
			'fas fa-umbrella',
			'fas fa-tv',
			'fas fa-truck',
			'fas fa-trophy',
			'fas fa-tree',
			'fas fa-trash-alt',
			'fas fa-trash',
			'fas fa-train',
			'fas fa-toolbox',
			'fas fa-ticket-alt',
			'fas fa-thumbtack',
			'fas fa-taxi',
			'fas fa-tags',
			'fas fa-tag',
			'fas fa-tachometer-alt',
			'fas fa-tablet-alt',
			'fas fa-tablet',
			'fas fa-sun',
			'fas fa-suitcase',
			'fas fa-subway',
			'fas fa-stroopwafel',
			'fas fa-stopwatch',
			'fas fa-sticky-note',
			'fas fa-star',
			'fas fa-space-shuttle',
			'fas fa-snowflake',
			'fas fa-skull-crossbones',
			'fas fa-shower',
			'fas fa-shopping-cart',
			'fas fa-shopping-basket',
			'fas fa-shopping-bag',
			'fas fa-shield-alt',
			'fas fa-search',
			'fas fa-scroll',
			'fas fa-screwdriver',
			'fas fa-school',
			'fas fa-save',
			'fas fa-ruler-vertical',
			'fas fa-ruler-horizontal',
			'fas fa-ruler-combined',
			'fas fa-rocket',
			'fas fa-road',
			'fas fa-ring',
			'fas fa-puzzle-piece',
			'fas fa-print',
			'fas fa-plug',
			'fas fa-plane',
			'fas fa-phone',
			'fas fa-pencil-alt',
			'fas fa-pen-nib',
			'fas fa-pen-fancy',
			'fas fa-pen-alt',
			'fas fa-pen',
			'fas fa-paw',
			'fas fa-paste',
			'fas fa-paperclip',
			'fas fa-paper-plane',
			'fas fa-paint-brush',
			'fas fa-newspaper',
			'fas fa-motorcycle',
			'fas fa-moon',
			'fas fa-money-check-alt',
			'fas fa-money-check',
			'fas fa-money-bill-alt',
			'fas fa-money-bill',
			'fas fa-mobile-alt',
			'fas fa-mobile',
			'fas fa-microphone-alt',
			'fas fa-microphone',
			'fas fa-memory',
			'fas fa-medkit',
			'fas fa-medal',
			'fas fa-marker',
			'fas fa-map-signs',
			'fas fa-map-pin',
			'fas fa-map-marker-alt',
			'fas fa-map-marker',
			'fas fa-map',
			'fas fa-magnet',
			'fas fa-magic',
			'fas fa-lock-open',
			'fas fa-lock',
			'fas fa-lightbulb',
			'fas fa-life-ring',
			'fas fa-lemon',
			'fas fa-leaf',
			'fas fa-laptop',
			'fas fa-keyboard',
			'fas fa-key',
			'fas fa-industry',
			'fas fa-images',
			'fas fa-image',
			'fas fa-hourglass',
			'fas fa-hospital',
			'fas fa-home',
			'fas fa-highlighter',
			'fas fa-helicopter',
			'fas fa-heart',
			'fas fa-headset',
			'fas fa-headphones-alt',
			'fas fa-headphones',
			'fas fa-hdd',
			'fas fa-hat-wizard',
			'fas fa-graduation-cap',
			'fas fa-globe',
			'fas fa-glasses',
			'fas fa-glass-martini',
			'fas fa-gift',
			'fas fa-gem',
			'fas fa-gavel',
			'fas fa-gamepad',
			'fas fa-futbol',
			'fas fa-flask',
			'fas fa-flag-checkered',
			'fas fa-flag',
			'fas fa-fire-extinguisher',
			'fas fa-fire',
			'fas fa-film',
			'fas fa-file-prescription',
			'fas fa-file-alt',
			'fas fa-file',
			'fas fa-fighter-jet',
			'fas fa-feather-alt',
			'fas fa-feather',
			'fas fa-fax',
			'fas fa-eye-dropper',
			'fas fa-eye',
			'fas fa-eraser',
			'fas fa-envelope-open',
			'fas fa-envelope',
			'fas fa-drum-steelpan',
			'fas fa-drum',
			'fas fa-door-open',
			'fas fa-door-closed',
			'fas fa-digital-tachograph',
			'fas fa-dice-two',
			'fas fa-dice-three',
			'fas fa-dice-six',
			'fas fa-dice-one',
			'fas fa-dice-four',
			'fas fa-dice-five',
			'fas fa-dice-d6',
			'fas fa-dice-d20',
			'fas fa-dice',
			'fas fa-cut',
			'fas fa-cubes',
			'fas fa-cube',
			'fas fa-copy',
			'fas fa-cookie-bite',
			'fas fa-cookie',
			'fas fa-compass',
			'fas fa-cogs',
			'fas fa-cog',
			'fas fa-coffee',
			'fas fa-cloud',
			'fas fa-clipboard',
			'fas fa-church',
			'fas fa-car',
			'fas fa-camera-retro',
			'fas fa-camera',
			'fas fa-calendar-alt',
			'fas fa-calendar',
			'fas fa-calculator',
			'fas fa-bus',
			'fas fa-bullseye',
			'fas fa-bullhorn',
			'fas fa-building',
			'fas fa-bug',
			'fas fa-broadcast-tower',
			'fas fa-briefcase',
			'fas fa-bookmark',
			'fas fa-book-dead',
			'fas fa-book',
			'fas fa-bomb',
			'fas fa-blender',
			'fas fa-birthday-cake',
			'fas fa-binoculars',
			'fas fa-bicycle',
			'fas fa-bell',
			'fas fa-beer',
			'fas fa-bed',
			'fas fa-bath',
			'fas fa-balance-scale',
			'fas fa-award',
			'fas fa-archive',
			'fas fa-anchor',
			'fas fa-ambulance',
			'far fa-trash-alt',
			'far fa-sun',
			'far fa-sticky-note',
			'far fa-star',
			'far fa-snowflake',
			'far fa-save',
			'far fa-paper-plane',
			'far fa-newspaper',
			'far fa-moon',
			'far fa-money-bill-alt',
			'far fa-map',
			'far fa-lightbulb',
			'far fa-life-ring',
			'far fa-lemon',
			'far fa-keyboard',
			'far fa-images',
			'far fa-image',
			'far fa-hourglass',
			'far fa-hospital',
			'far fa-heart',
			'far fa-hdd',
			'far fa-gem',
			'far fa-futbol',
			'far fa-flag',
			'far fa-file-alt',
			'far fa-file',
			'far fa-eye',
			'far fa-envelope-open',
			'far fa-envelope',
			'far fa-copy',
			'far fa-compass',
			'far fa-clipboard',
			'far fa-calendar-alt',
			'far fa-calendar',
			'far fa-building',
			'far fa-bookmark',
			'far fa-bell',
		],
		'Payments & Shopping': [
			'fas fa-trophy',
			'fas fa-thumbs-up',
			'fas fa-thumbs-down',
			'fas fa-tags',
			'fas fa-tag',
			'fas fa-star',
			'fas fa-shopping-cart',
			'fas fa-shopping-basket',
			'fas fa-shopping-bag',
			'fas fa-receipt',
			'fas fa-money-check-alt',
			'fas fa-money-check',
			'fas fa-key',
			'fas fa-heart',
			'fas fa-handshake',
			'fas fa-gift',
			'fas fa-gem',
			'fas fa-credit-card',
			'fas fa-certificate',
			'fas fa-cart-plus',
			'fas fa-cart-arrow-down',
			'fas fa-camera-retro',
			'fas fa-camera',
			'fas fa-bullhorn',
			'fas fa-bookmark',
			'fas fa-bell',
			'fab fa-stripe-s',
			'fab fa-stripe',
			'fab fa-paypal',
			'fab fa-google-wallet',
			'fab fa-ethereum',
			'fab fa-cc-visa',
			'fab fa-cc-stripe',
			'fab fa-cc-paypal',
			'fab fa-cc-mastercard',
			'fab fa-cc-jcb',
			'fab fa-cc-discover',
			'fab fa-cc-diners-club',
			'fab fa-cc-apple-pay',
			'fab fa-cc-amex',
			'fab fa-cc-amazon-pay',
			'fab fa-btc',
			'fab fa-bitcoin',
			'fab fa-apple-pay',
			'fab fa-amazon-pay',
			'fab fa-alipay',
			'far fa-thumbs-up',
			'far fa-thumbs-down',
			'far fa-star',
			'far fa-heart',
			'far fa-handshake',
			'far fa-gem',
			'far fa-credit-card',
			'far fa-bookmark',
			'far fa-bell',
		],
		'Pharmacy': [	
			'fas fa-vials',
			'fas fa-vial',
			'fas fa-thermometer',
			'fas fa-tablets',
			'fas fa-syringe',
			'fas fa-skull-crossbones',
			'fas fa-receipt',
			'fas fa-prescription-bottle-alt',
			'fas fa-prescription-bottle',
			'fas fa-prescription',
			'fas fa-pills',
			'fas fa-notes-medical',
			'fas fa-mortar-pestle',
			'fas fa-joint',
			'fas fa-history',
			'fas fa-flask',
			'fas fa-first-aid',
			'fas fa-file-prescription',
			'fas fa-file-medical',
			'fas fa-eye-dropper',
			'fas fa-capsules',
			'fas fa-cannabis',
			'fas fa-band-aid',
		],	
		'Political': [	
			'fas fa-piggy-bank',
			'fas fa-handshake',
			'fas fa-fist-raised',
			'fas fa-dove',
			'fas fa-donate',
			'fas fa-check-double',
			'fas fa-bullhorn',
			'fas fa-balance-scale',
			'fas fa-award',
			'far fa-handshake',
		],
		'Religion': [
			'fas fa-yin-yang',
			'fas fa-vihara',
			'fas fa-torii-gate',
			'fas fa-torah',
			'fas fa-synagogue',
			'fas fa-star-of-david',
			'fas fa-star-and-crescent',
			'fas fa-quran',
			'fas fa-praying-hands',
			'fas fa-pray',
			'fas fa-place-of-worship',
			'fas fa-peace',
			'fas fa-pastafarianism',
			'fas fa-om',
			'fas fa-mosque',
			'fas fa-menorah',
			'fas fa-khanda',
			'fas fa-kaaba',
			'fas fa-journal-whills',
			'fas fa-jedi',
			'fas fa-hanukiah',
			'fas fa-hamsa',
			'fas fa-gopuram',
			'fas fa-dove',
			'fas fa-dharmachakra',
			'fas fa-cross',
			'fas fa-church',
			'fas fa-bible',
			'fas fa-atom',
			'fas fa-ankh',
		],	
		'Science': [
			'fas fa-vials',
			'fas fa-vial',
			'fas fa-tablets',
			'fas fa-syringe',
			'fas fa-skull-crossbones',
			'fas fa-seedling',
			'fas fa-prescription-bottle',
			'fas fa-pills',
			'fas fa-mortar-pestle',
			'fas fa-microscope',
			'fas fa-magnet',
			'fas fa-frog',
			'fas fa-flask',
			'fas fa-fire',
			'fas fa-filter',
			'fas fa-eye-dropper',
			'fas fa-dna',
			'fas fa-clipboard-check',
			'fas fa-capsules',
			'fas fa-burn',
			'fas fa-brain',
			'fas fa-atom',
		],
		'Science Fiction': [	
			'fas fa-user-astronaut',
			'fas fa-space-shuttle',
			'fas fa-rocket',
			'fas fa-robot',
			'fas fa-moon',
			'fas fa-journal-whills',
			'fas fa-jedi',
			'fas fa-hand-spock',
			'fas fa-globe',
			'fas fa-atom',
			'fab fa-old-republic',
			'fab fa-jedi-order',
			'fab fa-galactic-senate',
			'fab fa-galactic-republic',
			'far fa-moon',
			'far fa-hand-spock',
		],	
		'Security': [
			'fas fa-user-shield',
			'fas fa-user-secret',
			'fas fa-user-lock',
			'fas fa-unlock-alt',
			'fas fa-unlock',
			'fas fa-shield-alt',
			'fas fa-passport',
			'fas fa-mask',
			'fas fa-lock-open',
			'fas fa-lock',
			'fas fa-key',
			'fas fa-id-card-alt',
			'fas fa-id-card',
			'fas fa-id-badge',
			'fas fa-fingerprint',
			'fas fa-file-signature',
			'fas fa-file-contract',
			'fas fa-eye-slash',
			'fas fa-eye',
			'fas fa-dungeon',
			'fas fa-door-open',
			'fas fa-door-closed',
			'fas fa-bug',
			'fas fa-ban',
			'far fa-id-card',
			'far fa-id-badge',
			'far fa-eye-slash',
			'far fa-eye',
	   ],
	   'Shapes': [
			'fas fa-star',
			'fas fa-square',
			'fas fa-shapes',
			'fas fa-play',
			'fas fa-map-marker',
			'fas fa-heart',
			'fas fa-folder',
			'fas fa-file',
			'fas fa-comment',
			'fas fa-cloud',
			'fas fa-circle',
			'fas fa-certificate',
			'fas fa-calendar',
			'fas fa-bookmark',
			'far fa-star',
			'far fa-square',
			'far fa-heart',
			'far fa-folder',
			'far fa-file',
			'far fa-comment',
			'far fa-circle',
			'far fa-calendar',
			'far fa-bookmark',
	   ],
	   'Shopping': [
			'fas fa-tshirt',
			'fas fa-truck',
			'fas fa-store-alt',
			'fas fa-store',
			'fas fa-shopping-cart',
			'fas fa-shopping-basket',
			'fas fa-shopping-bag',
			'fas fa-shipping-fast',
			'fas fa-receipt',
			'fas fa-gift',
			'fas fa-cart-plus',
			'fas fa-cart-arrow-down',
			'fas fa-barcode',
	   ],
	   'Social': [
			'fas fa-video',
			'fas fa-users',
			'fas fa-user-plus',
			'fas fa-user-friends',
			'fas fa-user-circle',
			'fas fa-user',
			'fas fa-thumbtack',
			'fas fa-thumbs-up',
			'fas fa-thumbs-down',
			'fas fa-star',
			'fas fa-share-square',
			'fas fa-share-alt',
			'fas fa-share',
			'fas fa-retweet',
			'fas fa-poll-h',
			'fas fa-poll',
			'fas fa-map-marker-alt',
			'fas fa-map-marker',
			'fas fa-images',
			'fas fa-image',
			'fas fa-heart',
			'fas fa-hashtag',
			'fas fa-envelope',
			'fas fa-comment-alt',
			'fas fa-comment',
			'fas fa-camera',
			'fas fa-birthday-cake',
			'fas fa-bell',
			'far fa-user-circle',
			'far fa-user',
			'far fa-thumbs-up',
			'far fa-thumbs-down',
			'far fa-star',
			'far fa-share-square',
			'far fa-images',
			'far fa-image',
			'far fa-heart',
			'far fa-envelope',
			'far fa-comment-alt',
			'far fa-comment',
			'far fa-bell',       
	   ],
	   'Spinners': [
			'fas fa-yin-yang',
			'fas fa-sync-alt',
			'fas fa-sync',
			'fas fa-sun',
			'fas fa-stroopwafel',
			'fas fa-spinner',
			'fas fa-snowflake',
			'fas fa-slash',
			'fas fa-ring',
			'fas fa-palette',
			'fas fa-life-ring',
			'fas fa-dharmachakra',
			'fas fa-crosshairs',
			'fas fa-compass',
			'fas fa-compact-disc',
			'fas fa-cog',
			'fas fa-circle-notch',
			'fas fa-certificate',
			'fas fa-bahai',
			'fas fa-atom',
			'fas fa-asterisk',
			'far fa-sun',
			'far fa-snowflake',
			'far fa-life-ring',
			'far fa-compass',
	   ],  
	   'Sports': [
			'fas fa-volleyball-ball',
			'fas fa-table-tennis',
			'fas fa-swimmer',
			'fas fa-running',
			'fas fa-quidditch',
			'fas fa-hockey-puck',
			'fas fa-golf-ball',
			'fas fa-futbol',
			'fas fa-football-ball',
			'fas fa-dumbbell',
			'fas fa-bowling-ball',
			'fas fa-basketball-ball',
			'fas fa-baseball-ball',
			'far fa-futbol',
	   ],   
	   'Spring': [
			'fas fa-umbrella',
			'fas fa-seedling',
			'fas fa-frog',
			'fas fa-cloud-sun',
			'fas fa-broom',
			'fas fa-allergies',
	   ],
	   'Status': [
			'fas fa-wifi',
			'fas fa-volume-up',
			'fas fa-volume-off',
			'fas fa-volume-mute',
			'fas fa-volume-down',
			'fas fa-video-slash',
			'fas fa-video',
			'fas fa-user-slash',
			'fas fa-user-alt-slash',
			'fas fa-user-alt',
			'fas fa-user',
			'fas fa-unlock-alt',
			'fas fa-unlock',
			'fas fa-toggle-on',
			'fas fa-toggle-off',
			'fas fa-tint-slash',
			'fas fa-tint',
			'fas fa-thumbs-up',
			'fas fa-thumbs-down',
			'fas fa-thermometer-three-quarters',
			'fas fa-thermometer-quarter',
			'fas fa-thermometer-half',
			'fas fa-thermometer-full',
			'fas fa-thermometer-empty',
			'fas fa-stream',
			'fas fa-star-half-alt',
			'fas fa-star-half',
			'fas fa-star',
			'fas fa-smoking-ban',
			'fas fa-signal',
			'fas fa-sign-out-alt',
			'fas fa-sign-in-alt',
			'fas fa-shopping-cart',
			'fas fa-shield-alt',
			'fas fa-question-circle',
			'fas fa-question',
			'fas fa-print',
			'fas fa-plus-square',
			'fas fa-plus-circle',
			'fas fa-plus',
			'fas fa-phone-slash',
			'fas fa-phone',
			'fas fa-parking',
			'fas fa-minus-square',
			'fas fa-minus-circle',
			'fas fa-minus',
			'fas fa-microphone-slash',
			'fas fa-microphone-alt-slash',
			'fas fa-microphone-alt',
			'fas fa-microphone',
			'fas fa-map-marker-alt',
			'fas fa-map-marker',
			'fas fa-lock-open',
			'fas fa-lock',
			'fas fa-lightbulb',
			'fas fa-info-circle',
			'fas fa-info',
			'fas fa-gas-pump',
			'fas fa-folder-open',
			'fas fa-folder',
			'fas fa-file-alt',
			'fas fa-file',
			'fas fa-eye-slash',
			'fas fa-eye',
			'fas fa-exclamation-triangle',
			'fas fa-exclamation-circle',
			'fas fa-exclamation',
			'fas fa-door-open',
			'fas fa-door-closed',
			'fas fa-compass',
			'fas fa-comment-slash',
			'fas fa-comment-alt',
			'fas fa-comment',
			'fas fa-cart-plus',
			'fas fa-cart-arrow-down',
			'fas fa-calendar-times',
			'fas fa-calendar-plus',
			'fas fa-calendar-minus',
			'fas fa-calendar-check',
			'fas fa-calendar-alt',
			'fas fa-calendar',
			'fas fa-bell-slash',
			'fas fa-bell',
			'fas fa-battery-three-quarters',
			'fas fa-battery-quarter',
			'fas fa-battery-half',
			'fas fa-battery-full',
			'fas fa-battery-empty',
			'fas fa-ban',
			'far fa-user',
			'far fa-thumbs-up',
			'far fa-thumbs-down',
			'far fa-star-half',
			'far fa-star',
			'far fa-question-circle',
			'far fa-plus-square',
			'far fa-minus-square',
			'far fa-lightbulb',
			'far fa-folder-open',
			'far fa-folder',
			'far fa-file-alt',
			'far fa-file',
			'far fa-eye-slash',
			'far fa-eye',
			'far fa-compass',
			'far fa-comment-alt',
			'far fa-comment',
			'far fa-calendar-times',
			'far fa-calendar-plus',
			'far fa-calendar-minus',
			'far fa-calendar-check',
			'far fa-calendar-alt',
			'far fa-calendar',
			'far fa-bell-slash',
			'far fa-bell',
	   ],    
		'Summer': [
			'fas fa-volleyball-ball',
			'fas fa-umbrella-beach',
			'fas fa-swimming-pool',
			'fas fa-swimmer',
			'fas fa-sun',
			'fas fa-lemon',
			'fas fa-fish',
			'fas fa-anchor',
			'far fa-sun',
			'far fa-lemon',
		],  
		'Toggle': [
			'fas fa-wifi',
			'fas fa-toggle-on',
			'fas fa-toggle-off',
			'fas fa-star-half-alt',
			'fas fa-star-half',
			'fas fa-star',
			'fas fa-microphone-slash',
			'fas fa-microphone',
			'fas fa-dot-circle',
			'fas fa-circle',
			'fas fa-check-circle',
			'fas fa-bullseye',
			'far fa-star-half',
			'far fa-star',
			'far fa-dot-circle',
			'far fa-circle',
			'far fa-check-circle',
		],
		'Travel': [
			'fas fa-wine-glass-alt',
			'fas fa-wine-glass',
			'fas fa-umbrella-beach',
			'fas fa-tv',
			'fas fa-taxi',
			'fas fa-swimming-pool',
			'fas fa-swimmer',
			'fas fa-suitcase-rolling',
			'fas fa-suitcase',
			'fas fa-spa',
			'fas fa-shuttle-van',
			'fas fa-plane-departure',
			'fas fa-plane-arrival',
			'fas fa-plane',
			'fas fa-passport',
			'fas fa-monument',
			'fas fa-map-marked-alt',
			'fas fa-map-marked',
			'fas fa-map',
			'fas fa-luggage-cart',
			'fas fa-hotel',
			'fas fa-hot-tub',
			'fas fa-globe-europe',
			'fas fa-globe-asia',
			'fas fa-globe-americas',
			'fas fa-globe-africa',
			'fas fa-glass-martini-alt',
			'fas fa-glass-martini',
			'fas fa-dumbbell',
			'fas fa-concierge-bell',
			'fas fa-cocktail',
			'fas fa-bus-alt',
			'fas fa-bus',
			'fas fa-bed',
			'fas fa-atlas',
			'fas fa-archway',
			'far fa-map',
		],	
		'Users & people': [
			'fas fa-wheelchair',
			'fas fa-walking',
			'fas fa-users-cog',
			'fas fa-users',
			'fas fa-user-times',
			'fas fa-user-tie',
			'fas fa-user-tag',
			'fas fa-user-slash',
			'fas fa-user-shield',
			'fas fa-user-secret',
			'fas fa-user-plus',
			'fas fa-user-ninja',
			'fas fa-user-minus',
			'fas fa-user-md',
			'fas fa-user-lock',
			'fas fa-user-injured',
			'fas fa-user-graduate',
			'fas fa-user-friends',
			'fas fa-user-edit',
			'fas fa-user-cog',
			'fas fa-user-clock',
			'fas fa-user-circle',
			'fas fa-user-check',
			'fas fa-user-astronaut',
			'fas fa-user-alt-slash',
			'fas fa-user-alt',
			'fas fa-user',
			'fas fa-swimmer',
			'fas fa-street-view',
			'fas fa-smile',
			'fas fa-running',
			'fas fa-pray',
			'fas fa-power-off',
			'fas fa-portrait',
			'fas fa-poo',
			'fas fa-people-carry',
			'fas fa-meh',
			'fas fa-male',
			'fas fa-id-card-alt',
			'fas fa-id-card',
			'fas fa-id-badge',
			'fas fa-hiking',
			'fas fa-frown',
			'fas fa-female',
			'fas fa-child',
			'fas fa-chalkboard-teacher',
			'fas fa-blind',
			'fas fa-bed',
			'fas fa-address-card',
			'fas fa-address-book',
			'fab fa-accessible-icon',
			'far fa-user-circle',
			'far fa-user',
			'far fa-smile',
			'far fa-meh',
			'far fa-id-card',
			'far fa-id-badge',
			'far fa-frown',
			'far fa-address-card',
			'far fa-address-book',
		],
		'Vehicles': [
			'fas fa-wheelchair',
			'fas fa-truck-pickup',
			'fas fa-truck-monster',
			'fas fa-truck',
			'fas fa-train',
			'fas fa-tractor',
			'fas fa-taxi',
			'fas fa-subway',
			'fas fa-space-shuttle',
			'fas fa-shuttle-van',
			'fas fa-shopping-cart',
			'fas fa-ship',
			'fas fa-rocket',
			'fas fa-plane',
			'fas fa-paper-plane',
			'fas fa-motorcycle',
			'fas fa-horse',
			'fas fa-helicopter',
			'fas fa-fighter-jet',
			'fas fa-car-side',
			'fas fa-car-crash',
			'fas fa-car-alt',
			'fas fa-car',
			'fas fa-bus-alt',
			'fas fa-bus',
			'fas fa-bicycle',
			'fas fa-ambulance',
			'far fa-paper-plane',
			'fab fa-accessible-icon',
		],
		'Weather': [
			'fas fa-wind',
			'fas fa-umbrella',
			'fas fa-sun',
			'fas fa-snowflake',
			'fas fa-moon',
			'fas fa-cloud-sun',
			'fas fa-cloud-moon',
			'fas fa-cloud',
			'fas fa-bolt',
			'far fa-sun',
			'far fa-snowflake',
			'far fa-moon',
		],
		'Writing': [
			'fas fa-thumbtack',
			'fas fa-sticky-note',
			'fas fa-quote-right',
			'fas fa-quote-left',
			'fas fa-pencil-alt',
			'fas fa-pen-square',
			'fas fa-pen-alt',
			'fas fa-pen',
			'fas fa-paragraph',
			'fas fa-paperclip',
			'fas fa-paper-plane',
			'fas fa-newspaper',
			'fas fa-keyboard',
			'fas fa-folder-open',
			'fas fa-folder',
			'fas fa-file-alt',
			'fas fa-file',
			'fas fa-eraser',
			'fas fa-envelope-open',
			'fas fa-envelope',
			'fas fa-edit',
			'fas fa-bookmark',
			'fas fa-book',
			'fas fa-archive',
			'far fa-sticky-note',
			'far fa-paper-plane',
			'far fa-newspaper',
			'far fa-keyboard',
			'far fa-folder-open',
			'far fa-folder',
			'far fa-file-alt',
			'far fa-file',
			'far fa-envelope-open',
			'far fa-envelope',
			'far fa-edit',
			'far fa-bookmark',
		],
	};

	const handleIconClick = (selectedIcon) => {
		setAttributes({ icon: selectedIcon });
		setIsModalOpen(false);
	};

	const handleSVGSave = () => {
        // setAttributes({ icon: tempSVG, iconType: 'customsvg' });
        setIsModalOpen(false);
    };

	const filterIcons = (iconArray) =>
	iconArray.filter((name) =>
		name.toLowerCase().includes(searchTerm.toLowerCase())
	);

	const getStyleVariable = () => {
	const variables = {};

	// Handle link color
	const linkColorToken = style?.elements?.link?.color?.text;
		if (linkColorToken) {
			const match = linkColorToken.match(/var:preset\|color\|(.+)/);
			if (match) {
				variables['--var-svg-color'] = `var(--wp--preset--color--${match[1]})`;
			}
		}

		// Handle font size
		const fontSizeToken = style?.typography?.fontSize;
		if (fontSizeToken) {
			const match = fontSizeToken.match(/var:preset\|font-size\|(.+)/);
			if (match) {
				variables['--var-svg-font-size'] = `var(--wp--preset--font-size--${match[1]})`;
			} else {
				// If it's a raw value like '20px', use it directly
				variables['--var-svg-font-size'] = fontSizeToken;
			}
		}

		return variables;
	};
	const blockProps = useBlockProps({
		style: getStyleVariable(),
	});

	return (
		<div { ...blockProps }>
			<Fragment>
				<BlockControls>
					<AlignmentControl
						value={textAlign}
						onChange={ ( nextAlign ) => {
							setAttributes( { textAlign: nextAlign } );
						} }
					/>
				</BlockControls>
				<InspectorControls key="setting">
					<PanelBody title={ __( 'Select Icon', "wens-haelo-pro" ) } initialOpen={false}>
						<Button
							id="icon-picker"
							variant="primary"
							onClick={() => setIsModalOpen(true)}
						>
							{__('Pick Icon', 'icon-picker')}
						</Button>
		
						<TextControl
							label={__("Link", "wens-haelo-pro")}
							value={ linkURL }
							onChange={ ( value ) => setAttributes( { linkURL: value } ) }
						/>
					</PanelBody>
				</InspectorControls>
			</Fragment>
			{
					attributes.linkURL == "" || attributes.linkURL == null
						?
						<div id="wptravel-blocks-icon" style={ {textAlign: textAlign} } >
							{iconType === 'fontawesome' ? (
								<i className={icon} aria-hidden="true"></i>
							) : (
								<div
									className="custom-svg-icon"
									dangerouslySetInnerHTML={{ __html: customSVG }}
								/>
							)}
						</div>
						: 
						<div id="wptravel-blocks-icon" style={ {textAlign: textAlign} } >
							<a href={linkURL}>
								{iconType === 'fontawesome' ? (
									<i className={icon} aria-hidden="true"></i>
								) : (
									<div
										className="custom-svg-icon"
										dangerouslySetInnerHTML={{ __html: customSVG }}
									/>
								)}
							</a>
						</div>
				}

            {isModalOpen && (
                <Modal
                    title={__('Select an Icon Type', 'icon-picker')}
                    onRequestClose={() => setIsModalOpen(false)}
                    className="icon-picker-modal"
                >
                    <RadioControl
                        label={__('Icon Source', 'icon-picker')}
                        selected={iconType}
                        options={[
                            { label: 'Font Awesome', value: 'fontawesome' },
                            { label: 'Custom SVG', value: 'customsvg' },
                        ]}
                        onChange={(value) => {
                            setAttributes({ iconType: value });
                            if (value === 'customsvg') {
                                setTempSVG(iconType === 'customsvg' ? icon : '');
                            }
                        }}
                    />

                    {iconType === 'fontawesome' && (
                        <>
                            <TextControl
                                placeholder="Search icons..."
                                value={searchTerm}
                                onChange={setSearchTerm}
                            />
                            <div className="icon-group-container">
                                {Object.entries(icons).map(([group, icons]) => {
                                    const filtered = filterIcons(icons);
                                    if (!filtered.length) return null;
                                    return (
                                        <div key={group} className="icon-group">
                                            <h3>{group}</h3>
                                            <div className="icon-grid">
                                                {filtered.map((iconClass) => (
                                                    <Button
                                                        key={iconClass}
                                                        className="icon-button"
                                                        onClick={() => handleIconClick(iconClass)}
                                                    >
                                                        <i className={iconClass}></i>
                                                    </Button>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {iconType === 'customsvg' && (
                        <>
                            <TextareaControl
                                label="Enter SVG Markup"
                                value={customSVG}
                                onChange={(value) => setAttributes({ customSVG: value })}
                                rows={6}
                            />
                            <Button
                                variant="primary"
                                onClick={handleSVGSave}
                                className="svg-save-button"
                            >
                                Save SVG
                            </Button>
                        </>
                    )}
                </Modal>
            )}
		</div>
	);
}